import type { DragEndEvent } from '@dnd-kit/core';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import type { ReactNode } from 'react';

export interface SortableCriteriaListProps {
  /** Ordered, stable ids of the draggable items only — pinned items (index
   *  date, default analytical-dataset variables) are never included. */
  itemIds: string[];
  /** Called once per completed drag with the dragged item's id and the id
   *  of the item it was dropped on; never called for a no-op drop. */
  onReorder: (activeId: string, overId: string) => void;
  children: ReactNode;
}

/**
 * Shared drag-and-drop wrapper for the cohort-criteria and
 * analytical-dataset-variable card grids. Both render a CSS `auto-fill`
 * grid, so this uses `rectSortingStrategy` (grid/wrapping-aware) rather
 * than the list-oriented vertical strategy.
 */
export const SortableCriteriaList = ({
  itemIds,
  onReorder,
  children,
}: SortableCriteriaListProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      onReorder(String(active.id), String(over.id));
    }
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={itemIds} strategy={rectSortingStrategy}>
        {children}
      </SortableContext>
    </DndContext>
  );
};
